import { getLangPrefix, gettext } from './utils.js';

// Quote Auto-Save Functionality
class QuoteAutoSave {
    constructor(quoteId, options = {}) {
        this.quoteId = quoteId;
        this.saveInterval = options.saveInterval || 30000; // 30 seconds
        const prefix = getLangPrefix();
        this.saveUrl = options.saveUrl || `${prefix}/quotes/${quoteId}/auto-save/`;
        this.manualSaveUrl = options.manualSaveUrl || `${prefix}/quotes/${quoteId}/save-draft/`;
        this.lastSaveTime = null;
        this.hasChanges = false;
        this.saveTimer = null;
        this.isOnline = navigator.onLine;
        
        this.init();
    }
    
    init() {
        this.bindEvents();
        this.setupAutoSave();
        this.setupOfflineHandling();
    }
    
    bindEvents() {
        // Track form changes
        document.addEventListener('input', (e) => {
            if (e.target.closest('#quote-form')) {
                this.markAsChanged();
            }
        });
        
        document.addEventListener('change', (e) => {
            if (e.target.closest('#quote-form')) {
                this.markAsChanged();
            }
        });
        
        // The Save Draft button posts via its own hx-post; a click handler
        // here used to send a second, racing save-draft request. Only the
        // bookkeeping happens in JS: a finished save or a started Finalize
        // means there is nothing left to warn about on unload.
        document.body.addEventListener('htmx:afterRequest', (e) => {
            if (e.detail.elt && e.detail.elt.id === 'save-draft-btn' && e.detail.successful) {
                this.hasChanges = false;
                this.updateSaveButton();
            }
        });
        document.body.addEventListener('htmx:beforeRequest', (e) => {
            if (e.detail.elt && e.detail.elt.id === 'finalize-quote-btn') {
                this.hasChanges = false;
            }
        });
        
        // Handle form submission buttons to clear hasChanges flag
        const form = document.getElementById('quote-form');
        if (form) {
            form.addEventListener('submit', (e) => {
                // Clear hasChanges flag when form is submitted
                // This prevents the beforeunload warning
                this.hasChanges = false;
            });
        }
        
        // Handle save draft form button specifically
        document.addEventListener('click', (e) => {
            if (e.target && e.target.name === 'save_draft') {
                // Clear hasChanges flag when save draft button is clicked
                this.hasChanges = false;
            }
        });
        
        // Keyboard shortcut
        document.addEventListener('keydown', (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                this.saveManually();
            }
        });
        
        // Before unload warning
        window.addEventListener('beforeunload', (e) => {
            if (this.hasChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        });
    }
    
    setupAutoSave() {
        this.saveTimer = setInterval(() => {
            if (this.hasChanges) {
                this.autoSave();
            }
        }, this.saveInterval);
    }
    
    setupOfflineHandling() {
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.hideOfflineIndicator();
        });
        
        window.addEventListener('offline', () => {
            this.isOnline = false;
            this.showOfflineIndicator();
        });
    }
    
    markAsChanged() {
        this.hasChanges = true;
        this.updateSaveButton();
    }
    
    async autoSave() {
        if (!this.isOnline) {
            // Skip auto-save when offline
            return;
        }
        
        try {
            const formData = this.collectFormData();
            formData.append('auto_save', 'true');
            
            const response = await fetch(this.saveUrl, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-CSRFToken': this.getCSRFToken()
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                this.handleSaveSuccess(data);
            }
        } catch (error) {
            // Silent failure — auto-save will retry on next change
        }
    }
    
    async saveManually() {
        this.showSaving();
        
        try {
            const formData = this.collectFormData();
            formData.append('manual_save', 'true');
            
            // Use HTMX if available for manual saves to update UI
            if (typeof htmx !== 'undefined') {
                htmx.ajax('POST', this.manualSaveUrl, {
                    values: Object.fromEntries(formData),
                    target: '#save-status',
                    swap: 'innerHTML'
                });
            } else {
                // Fallback to regular fetch
                const response = await fetch(this.manualSaveUrl, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'X-CSRFToken': this.getCSRFToken(),
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });
                
                if (response.ok) {
                    const html = await response.text();
                    const saveStatus = document.getElementById('save-status');
                    if (saveStatus) {
                        saveStatus.innerHTML = html;
                    }
                }
            }
            
            this.hasChanges = false;
            this.updateSaveButton();
        } catch (error) {
            this.showSaveError();
        }
    }
    
    collectFormData() {
        const form = document.getElementById('quote-form');
        return new FormData(form);
    }
    
    
    updateSaveButton() {
        const saveBtn = document.getElementById('save-draft-btn');
        if (saveBtn) {
            saveBtn.disabled = !this.hasChanges;
            saveBtn.textContent = this.hasChanges ? gettext('Save Draft') : gettext('Saved');
        }
    }
    
    showSaving() {
        const status = document.getElementById('save-status');
        if (status) {
            status.innerHTML = `<span class="text-blue-600 animate-pulse">${gettext('Saving...')}</span>`;
        }
    }
    
    showSaveError() {
        const status = document.getElementById('save-status');
        if (status) {
            status.innerHTML = `<span class="text-red-600">${gettext('Save failed')}</span>`;
        }
    }
    
    handleSaveSuccess(data) {
        this.hasChanges = false;
        this.lastSaveTime = new Date(data.timestamp);
        this.updateSaveStatus();
    }
    
    updateSaveStatus() {
        const status = document.getElementById('save-status');
        if (status && this.lastSaveTime) {
            status.innerHTML = `
                <span class="text-green-600 flex items-center gap-1">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                    ${gettext('Saved')} ${this.formatRelativeTime(this.lastSaveTime)}
                </span>
            `;
        }
    }
    
    showOfflineIndicator() {
        const indicator = document.getElementById('offline-indicator');
        if (indicator) {
            indicator.classList.remove('hidden');
        }
    }
    
    hideOfflineIndicator() {
        const indicator = document.getElementById('offline-indicator');
        if (indicator) {
            indicator.classList.add('hidden');
        }
    }
    
    
    formatRelativeTime(date) {
        const rtf = new Intl.RelativeTimeFormat(document.documentElement.lang || 'en', { numeric: 'auto' });
        const diff = (new Date(date) - new Date()) / 1000;
        
        if (Math.abs(diff) < 60) return gettext('just now');
        if (Math.abs(diff) < 3600) return rtf.format(Math.round(diff / 60), 'minute');
        if (Math.abs(diff) < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
        return rtf.format(Math.round(diff / 86400), 'day');
    }
    
    getCSRFToken() {
        const token = document.querySelector('[name=csrfmiddlewaretoken]');
        return token ? token.value : '';
    }
    
    destroy() {
        // Clean up timers and event listeners
        if (this.saveTimer) {
            clearInterval(this.saveTimer);
        }
    }
}

// Export init function for module system
export function init() {
    const quoteForm = document.getElementById('quote-form');
    if (quoteForm && quoteForm.dataset.quoteId) {
        window.quoteAutoSave = new QuoteAutoSave(quoteForm.dataset.quoteId);
    }
}
